import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getVacancy, getVacantForward } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronDown, ChevronRight, ChevronLeft, AlertTriangle, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { VacancyRateTab } from '@/components/vacancy/VacancyRateTab';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatVacancyDate(dateStr) {
  if (!dateStr) return '-';
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
  return `${dayName}, ${month}/${day}`;
}
const FULL_MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function VacantForwardView({ units }) {
  const [sortMode, setSortMode] = useState('date');
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const sorted = useMemo(() => [...units].sort((a, b) => {
    if (a.is_currently_vacant && !b.is_currently_vacant) return -1;
    if (!a.is_currently_vacant && b.is_currently_vacant) return 1;
    return a.vacancy_start.localeCompare(b.vacancy_start);
  }), [units]);

  const { currentlyVacant, byMonth } = useMemo(() => {
    const cv = sorted.filter(v => v.is_currently_vacant);
    const upcoming = sorted.filter(v => !v.is_currently_vacant);
    const months = {};
    upcoming.forEach(v => {
      const d = new Date(v.vacancy_start + 'T00:00:00');
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${FULL_MONTH_NAMES[d.getMonth() + 1]} ${d.getFullYear()}`;
      if (!months[key]) months[key] = { label, items: [] };
      months[key].items.push(v);
    });
    return { currentlyVacant: cv, byMonth: months };
  }, [sorted]);

  const groupedByBuilding = useMemo(() => {
    const grouped = {};
    sorted.forEach(v => {
      const key = v.property_name || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(v);
    });
    return grouped;
  }, [sorted]);

  const VFRow = ({ v }) => (
    <TableRow className="hover:bg-muted/40">
      <TableCell className="font-medium">{v.property_name}</TableCell>
      <TableCell>Unit {v.unit_number}</TableCell>
      <TableCell className="tabular-nums">
        {v.is_currently_vacant
          ? <span className="font-bold text-red-600 tracking-wide">VACANT</span>
          : <span className="font-medium text-amber-700">{formatVacancyDate(v.vacancy_start)}</span>}
      </TableCell>
    </TableRow>
  );

  const tableHead = (
    <TableHeader><TableRow className="bg-muted/30">
      <TableHead className="text-xs font-semibold uppercase tracking-wide">Property</TableHead>
      <TableHead className="text-xs font-semibold uppercase tracking-wide">Unit</TableHead>
      <TableHead className="text-xs font-semibold uppercase tracking-wide">Next Available</TableHead>
    </TableRow></TableHeader>
  );

  const sortButtons = (
    <div className="flex gap-2 mb-3">
      <Button size="sm" variant={sortMode === 'date' ? 'default' : 'outline'} onClick={() => setSortMode('date')} className="text-xs gap-1">
        <ArrowUpDown className="h-3 w-3" /> Next Available
      </Button>
      <Button size="sm" variant={sortMode === 'building' ? 'default' : 'outline'} onClick={() => setSortMode('building')} className="text-xs gap-1">
        <ArrowUpDown className="h-3 w-3" /> By Building
      </Button>
    </div>
  );

  if (sortMode === 'date') {
    return (
      <div className="space-y-2">
        {sortButtons}

        {currentlyVacant.length > 0 && (
          <Card className="overflow-hidden border-red-200">
            <button className="w-full flex items-center justify-between p-3 hover:bg-red-50 transition-colors" onClick={() => toggle('__vacant__')}>
              <div className="flex items-center gap-2">
                {expanded['__vacant__'] === false ? <ChevronRight className="h-4 w-4 text-red-600" /> : <ChevronDown className="h-4 w-4 text-red-600" />}
                <span className="text-sm font-bold text-red-600">Currently Vacant</span>
                <Badge variant="destructive" className="text-xs">{currentlyVacant.length}</Badge>
              </div>
            </button>
            {expanded['__vacant__'] !== false && (
              <Table>
                {tableHead}
                <TableBody>{currentlyVacant.map(v => <VFRow key={v.unit_id} v={v} />)}</TableBody>
              </Table>
            )}
          </Card>
        )}

        {Object.entries(byMonth).sort((a, b) => a[0].localeCompare(b[0])).map(([monthKey, { label, items }]) => {
          const isOpen = expanded[monthKey] !== false;
          return (
            <Card key={monthKey} className="overflow-hidden">
              <button className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors" onClick={() => toggle(monthKey)}>
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="text-sm font-semibold">{label}</span>
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </div>
              </button>
              {isOpen && <Table>{tableHead}<TableBody>{items.map(v => <VFRow key={v.unit_id} v={v} />)}</TableBody></Table>}
            </Card>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sortButtons}
      {Object.entries(groupedByBuilding).sort((a, b) => a[0].localeCompare(b[0])).map(([propName, items]) => {
        const isOpen = expanded[propName] !== false;
        const hasVacant = items.some(v => v.is_currently_vacant);
        return (
          <Card key={propName} className={`overflow-hidden ${hasVacant ? 'border-red-200' : ''}`}>
            <button className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors" onClick={() => toggle(propName)}>
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span className="text-sm font-semibold">{propName}</span>
                <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                {hasVacant && <Badge variant="destructive" className="text-xs">Vacant Now</Badge>}
              </div>
            </button>
            {isOpen && <Table>{tableHead}<TableBody>{items.map(v => <VFRow key={v.unit_id} v={v} />)}</TableBody></Table>}
          </Card>
        );
      })}
    </div>
  );
}

function UpcomingVacanciesView({ vacancies }) {
  const [sortMode, setSortMode] = useState('property');
  const [expanded, setExpanded] = useState({});

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const VacancyRow = ({ v, idx }) => (
    <TableRow key={idx} className={`hover:bg-muted/40 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}>
      <TableCell className="font-medium">{v.property_name}</TableCell>
      <TableCell>Unit {v.unit_number}</TableCell>
      <TableCell className="tabular-nums">{formatVacancyDate(v.vacancy_start)}</TableCell>
      <TableCell>
        {v.has_future_tenant ? (
          <Badge variant="secondary" className="text-xs">Until {formatVacancyDate(v.vacancy_end)}</Badge>
        ) : (
          <Badge className="text-xs bg-amber-50 text-amber-900 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" />Vacant forward</Badge>
        )}
      </TableCell>
    </TableRow>
  );

  if (sortMode === 'property') {
    const grouped = {};
    vacancies.forEach(v => {
      const key = v.property_name || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(v);
    });
    Object.values(grouped).forEach(arr => arr.sort((a, b) => {
      const aNum = parseInt(a.unit_number, 10) || 999;
      const bNum = parseInt(b.unit_number, 10) || 999;
      return aNum - bNum;
    }));

    return (
      <div className="space-y-2">
        <div className="flex gap-2 mb-2">
          <Button size="sm" variant={sortMode === 'property' ? 'default' : 'outline'} onClick={() => setSortMode('property')} className="text-xs gap-1"><ArrowUpDown className="h-3 w-3" /> By Property</Button>
          <Button size="sm" variant={sortMode === 'date' ? 'default' : 'outline'} onClick={() => setSortMode('date')} className="text-xs gap-1"><ArrowUpDown className="h-3 w-3" /> By Date</Button>
        </div>
        {Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([propName, items]) => {
          const isOpen = expanded[propName] !== false;
          return (
            <Card key={propName} className="overflow-hidden">
              <button className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors" onClick={() => toggle(propName)}>
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="text-sm font-semibold">{propName}</span>
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </div>
              </button>
              {isOpen && (
                <Table>
                  <TableHeader><TableRow className="bg-muted/30">
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Property</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Unit</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Vacancy Starts</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{items.map(v => <VacancyRow key={`${v.unit_id}-${v.vacancy_start}`} v={v} idx={0} />)}</TableBody>
                </Table>
              )}
            </Card>
          );
        })}
      </div>
    );
  }

  // Sort by date with collapsible months
  const sortedByDate = [...vacancies].sort((a, b) => a.vacancy_start.localeCompare(b.vacancy_start));
  const byMonth = {};
  sortedByDate.forEach(v => {
    const d = new Date(v.vacancy_start + 'T00:00:00');
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${FULL_MONTH_NAMES[d.getMonth() + 1]} ${d.getFullYear()}`;
    if (!byMonth[key]) byMonth[key] = { label, items: [] };
    byMonth[key].items.push(v);
  });

  return (
    <div className="space-y-2">
      <div className="flex gap-2 mb-2">
        <Button size="sm" variant={sortMode === 'property' ? 'default' : 'outline'} onClick={() => setSortMode('property')} className="text-xs gap-1"><ArrowUpDown className="h-3 w-3" /> By Property</Button>
        <Button size="sm" variant={sortMode === 'date' ? 'default' : 'outline'} onClick={() => setSortMode('date')} className="text-xs gap-1"><ArrowUpDown className="h-3 w-3" /> By Date</Button>
      </div>
      {Object.entries(byMonth).sort((a, b) => a[0].localeCompare(b[0])).map(([monthKey, { label, items }]) => {
        const isOpen = expanded[monthKey] !== false;
        return (
          <Card key={monthKey} className="overflow-hidden">
            <button className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors" onClick={() => toggle(monthKey)}>
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span className="text-sm font-semibold">{label}</span>
                <Badge variant="secondary" className="text-xs">{items.length}</Badge>
              </div>
            </button>
            {isOpen && (
              <Table>
                <TableHeader><TableRow className="bg-muted/30">
                  <TableHead className="text-xs font-semibold uppercase tracking-wide">Property</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide">Unit</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide">Vacancy Starts</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>{items.map(v => <VacancyRow key={`${v.unit_id}-${v.vacancy_start}`} v={v} idx={0} />)}</TableBody>
              </Table>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function UpcomingVacanciesTab({ allVacancies }) {
  const [subView, setSubView] = useState('vacant_forward');
  const [vfUnits, setVfUnits] = useState([]);
  const [vfLoading, setVfLoading] = useState(true);

  useEffect(() => {
    setVfLoading(true);
    getVacantForward()
      .then(d => setVfUnits(d.vacant_forward || []))
      .catch(() => setVfUnits([]))
      .finally(() => setVfLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 pb-3 border-b border-border/50">
        <Button size="sm" variant={subView === 'vacant_forward' ? 'default' : 'outline'} onClick={() => setSubView('vacant_forward')} data-testid="subview-vacant-forward-btn">
          Vacant Forward
        </Button>
        <Button size="sm" variant={subView === 'all' ? 'default' : 'outline'} onClick={() => setSubView('all')} data-testid="subview-all-vacancies-btn">
          All Vacancies
        </Button>
      </div>

      {subView === 'vacant_forward' ? (
        vfLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
        ) : vfUnits.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-sm text-muted-foreground">No units are currently vacant forward</p>
            </CardContent>
          </Card>
        ) : (
          <VacantForwardView units={vfUnits} />
        )
      ) : (
        <AllVacanciesSplitView allVacancies={allVacancies} />
      )}
    </div>
  );
}

function AllVacanciesSplitView({ allVacancies }) {
  const today = new Date();
  const twoWeeksOut = new Date(today);
  twoWeeksOut.setDate(twoWeeksOut.getDate() + 14);
  const todayStr = today.toISOString().split('T')[0];
  const twoWeeksStr = twoWeeksOut.toISOString().split('T')[0];

  const getDays = (v) => {
    if (!v.vacancy_end) return 9999;
    const s = new Date(v.vacancy_start + 'T00:00:00');
    const e = new Date(v.vacancy_end + 'T00:00:00');
    return Math.round((e - s) / (1000 * 60 * 60 * 24));
  };

  const hasWeekend = (startStr, endStr) => {
    const s = new Date(startStr + 'T00:00:00');
    const e = new Date(endStr + 'T00:00:00');
    const d = new Date(s);
    let hasSat = false, hasSun = false;
    while (d < e) {
      if (d.getDay() === 6) hasSat = true;
      if (d.getDay() === 0) hasSun = true;
      if (hasSat && hasSun) return true;
      d.setDate(d.getDate() + 1);
    }
    return false;
  };

  // Left: 30+ day vacancies
  const longTermVacancies = allVacancies.filter(v => getDays(v) >= 30);

  // Right: Airbnb Eligible — new rules:
  // 1. Between-vacancy gaps within 3 months, 5 day min UNLESS there's a weekend
  // 2. Vacant forward units within next 2 weeks
  const airbnbEligible = allVacancies.filter(v => {
    const days = getDays(v);

    // Rule 2: Vacant forward within 2 weeks
    if (!v.has_future_tenant) {
      return v.vacancy_start <= twoWeeksStr && v.vacancy_start >= todayStr;
    }

    // Rule 1: Between-vacancy gap
    if (days >= 5) return true;
    // Less than 5 days but has a weekend → still eligible
    if (days > 0 && v.vacancy_end && hasWeekend(v.vacancy_start, v.vacancy_end)) return true;
    return false;
  });

  const VacancyRow = ({ v }) => {
    const days = getDays(v);
    const daysLabel = days === 9999 ? '' : `${days}d`;
    return (
      <TableRow className="hover:bg-muted/40">
        <TableCell className="font-medium text-xs">{v.property_name}</TableCell>
        <TableCell className="text-xs">Unit {v.unit_number}</TableCell>
        <TableCell className="tabular-nums text-xs">{formatVacancyDate(v.vacancy_start)}</TableCell>
        <TableCell className="text-xs">
          {v.has_future_tenant ? (
            <span className="tabular-nums">{formatVacancyDate(v.vacancy_end)} <span className="text-muted-foreground">({daysLabel})</span></span>
          ) : (
            <Badge className="text-[10px] bg-amber-50 text-amber-900 border-amber-200"><AlertTriangle className="h-3 w-3 mr-1" />Open</Badge>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const tableHead = (
    <TableHeader><TableRow className="bg-muted/30">
      <TableHead className="text-[10px] font-semibold uppercase tracking-wide">Property</TableHead>
      <TableHead className="text-[10px] font-semibold uppercase tracking-wide">Unit</TableHead>
      <TableHead className="text-[10px] font-semibold uppercase tracking-wide">Starts</TableHead>
      <TableHead className="text-[10px] font-semibold uppercase tracking-wide">Ends</TableHead>
    </TableRow></TableHeader>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="vacancy-split-view">
      {/* LEFT: Long-term vacancies (30+ days) */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            Upcoming Vacancies
            <Badge variant="secondary" className="text-xs tabular-nums">{longTermVacancies.length}</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">30+ day vacancies</p>
        </CardHeader>
        <CardContent className="p-0">
          {longTermVacancies.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">No vacancies 30+ days</div>
          ) : (
            <Table>
              {tableHead}
              <TableBody>
                {longTermVacancies
                  .sort((a, b) => a.vacancy_start.localeCompare(b.vacancy_start))
                  .map(v => <VacancyRow key={`${v.unit_id}-${v.vacancy_start}`} v={v} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* RIGHT: Airbnb Eligible */}
      <Card className="overflow-hidden border-sky-200">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            Airbnb Eligible
            <Badge className="text-xs tabular-nums bg-sky-100 text-sky-800 border-sky-200">{airbnbEligible.length}</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">5+ days (or includes full weekend), between reservations or vacant forward within 2 weeks</p>
        </CardHeader>
        <CardContent className="p-0">
          {airbnbEligible.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">No Airbnb-eligible vacancies</div>
          ) : (
            <Table>
              {tableHead}
              <TableBody>
                {airbnbEligible
                  .sort((a, b) => a.vacancy_start.localeCompare(b.vacancy_start))
                  .map(v => <VacancyRow key={`airbnb-${v.unit_id}-${v.vacancy_start}`} v={v} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function VacancyPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'rate' ? 'rate' : 'upcoming';

  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getVacancy(year);
      setData(result);
    } catch {
      toast.error('Failed to load vacancy data');
    } finally {
      setLoading(false);
    }
    // getVacancy is a stable module import; year is the only runtime dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Vacancy</h1>
          <p className="text-sm text-muted-foreground mt-1">Track vacancies across your portfolio</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-heading text-lg font-semibold tabular-nums w-16 text-center">{year}</span>
          <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground">Loading...</div>
      ) : data ? (
        <Tabs defaultValue={initialTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming Vacancies</TabsTrigger>
            <TabsTrigger value="rate">Vacancy Rate</TabsTrigger>
          </TabsList>

          {/* UPCOMING VACANCIES (Vacant Forward + All) */}
          <TabsContent value="upcoming" className="space-y-4">
            <UpcomingVacanciesTab allVacancies={data.upcoming_vacancies} />
          </TabsContent>

          {/* VACANCY RATE */}
          <TabsContent value="rate" className="space-y-4">
            <VacancyRateTab data={data} />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}
